




















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { TypeformForm } from '@/api/model/typeform/typeform-form'
import { TypeformField } from '@/api/model/typeform/typeform-field'
import { TypeformLogicJump } from '@/api/model/typeform/typeform-logic-jump'
import { TypeformLogicJumpAction } from '@/api/model/typeform/typeform-logic-jump-action'
import { TypeformLogicJumpFlattenedCondition } from '@/api/model/typeform/typeform-logic-jump-flattened-condition'
import { TypeformMap } from '@/api/model/typeform/typeform-map'
import { cloneDeep, isEqual } from 'lodash'

@Component
export default class LogicJumpPopOutCondition extends Vue {
    @Prop({ required: true }) readonly form!: TypeformForm
    @Prop({ required: true }) readonly logicJump!: TypeformLogicJump
    @Prop({ required: true }) readonly action!: TypeformLogicJumpAction
    @Prop({ required: true }) readonly condition!: TypeformLogicJumpFlattenedCondition
    @Prop({ required: true }) readonly conditionIndex!: number
    internalCondition: TypeformLogicJumpFlattenedCondition = null
    deleteHover: boolean = false

    @Watch('condition', { immediate: true })
    onConditionChanged() {
        this.internalCondition = cloneDeep(this.condition)

        this.autoSelectLeftVar()
        this.autoSelectOp()
        this.autoSelectRightVar()
    }

    autoSelectLeftVar(): void {
        if (this.leftVar !== null) {
            return
        }

        this.leftVar = `${this.logicJump.type}-${this.logicJump.ref}`
    }

    @Watch('opOptions')
    autoSelectOp(): void {
        if (this.internalCondition.op !== null) {
            return
        }

        if (this.opOptions.length) {
            this.internalCondition.op = this.opOptions[0].value
        }
    }

    @Watch('rightVarOptions')
    autoSelectRightVar(): void {
        if (this.rightVar !== null) {
            return
        }

        if (this.fieldType === 'choice' && this.rightVarOptions.length) {
            this.rightVar = this.rightVarOptions[0].value
        } else if (this.fieldType === 'date') {
            this.rightVarValue = new Date().toISOString()
        } else if (this.fieldType === 'file') {
            this.rightVarValue = true
        }
    }

    @Watch('field')
    resetRightVar(newField, oldField): void {
        const newFieldType = TypeformLogicJumpFlattenedCondition.getFieldType(newField)
        const oldFieldType = TypeformLogicJumpFlattenedCondition.getFieldType(oldField)

        if (newFieldType === oldFieldType) {
           return
        } else if (newFieldType === 'numeric' && oldFieldType === 'text') {
            return
        } else if (newFieldType === 'text' && oldFieldType === 'numeric') {
            return
        }

        this.rightVar = null
        this.autoSelectRightVar()
    }

    @Watch('internalCondition', { deep: true })
    onInternalConditionChanged() {
        if (isEqual(this.internalCondition, this.condition)) {
            return
        }

        this.$emit('update:condition', this.internalCondition)
    }

    get flattenedOpOptions() {
        const ops = [
            'and',
            'or',
        ]

        return ops.map(op => ({
            label: this.$t(`company_settings.typeform_forms.logic_jump.op.${op}`),
            value: op,
        }))
    }

    get leftVar(): string|null {
        if (this.internalCondition.vars[0].type === null) {
            return null
        }

        return this.internalCondition.vars[0].type + '-' + this.internalCondition.vars[0].value
    }

    set leftVar(leftVar: string|null) {
        let type = null
        let value = null

        if (leftVar !== null) {
            const leftVarParts = leftVar.split('-')
            type = leftVarParts.shift()
            value = leftVarParts.join('-')
        }

        this.internalCondition.vars.splice(0, 1, { type, value })
    }

    get leftVarOptions() {
        let options = []

        if (this.field) {
            options = this.field.getAllowedReferenceOptions(this.form, (type, value) => `${type}-${value}`, true)
        } else {
            // All hidden fields
            this.form.hidden.forEach(hidden => {
                options.push({
                    label: hidden,
                    value: `hidden-${hidden}`,
                })
            })
        }

        return options
    }

    get opOptions() {
        const ops = TypeformLogicJumpFlattenedCondition.getFieldOps(this.field)

        return ops.map(op => ({
            label: this.$t(`company_settings.typeform_forms.logic_jump.op.${op}`),
            value: op,
        }))
    }

    get rightVar(): string|null {
        if (this.internalCondition.vars[1].type === null) {
            return null
        }

        return this.internalCondition.vars[1].type + '-' + this.internalCondition.vars[1].value
    }

    set rightVar(rightVar: string|null) {
        let type = null
        let value = null

        if (rightVar !== null) {
            const rightVarParts = rightVar.split('-')
            type = rightVarParts.shift()
            value = rightVarParts.join('-')
        }

        this.internalCondition.vars.splice(1, 1, { type, value })
    }

    get rightVarValue(): string|number|boolean|null {
        if (this.internalCondition.vars[1].type === null) {
            return null
        }

        return this.internalCondition.vars[1].value
    }

    set rightVarValue(rightVarValue: string|number|boolean|null) {
        let type = null
        let value = null

        if (rightVarValue !== null) {
            type = 'constant'
            value = rightVarValue

            if (this.fieldType === 'date') {
                value = value.substring(0, 10)
            }
        }

        this.internalCondition.vars.splice(1, 1, { type, value })
    }

    get rightVarOptions() {
        const fieldType = this.fieldType
        if (fieldType !== 'choice') {
            return []
        }

        if (this.field.type === 'yes_no') {
            return [
                { label: this.$t('yes'), value: 'constant-true' },
                { label: this.$t('no'), value: 'constant-false' },
            ]
        }

        const options = []

        const choices = this.field.properties.choices as TypeformMap[]
        choices.forEach(choice => {
            options.push({
                label: choice.label,
                value: `choice-${choice.ref}`,
            })
        })

        return options
    }

    get field(): TypeformField|null {
        return this.form.fields.find(field => field.ref === this.internalCondition.vars[0].value)
    }

    get fieldType(): string|null {
        return TypeformLogicJumpFlattenedCondition.getFieldType(this.field)
    }

    get isDeletable(): boolean {
        return this.action.flattenedConditions.length > 1
    }

    onDelete() {
        this.$emit('delete')
    }
}
